import { useEffect, useState } from 'react';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

// Firestore docs store STORAGE PATHS (maintenance/{aircraftId}/...), not getDownloadURL()
// token URLs — a leaked doc must never leak the file. These helpers resolve a
// stored reference to a downloadable URL at render time, gated by storage.rules.
//
// Back-compat passthrough: legacy/migrated docs and demo data may still hold
// full URLs ('https://...'), local previews ('blob:'/'data:') or the demo '#'
// placeholder — those are returned as-is.

function isStoragePath(ref: string): boolean {
  return ref !== '' && !/^(https?:|blob:|data:|#|\/)/.test(ref);
}

// Resolved URLs are cached so lists don't re-fetch on every render/snapshot.
const urlCache = new Map<string, string>();

/** Resolve a stored file reference to a URL. Never throws — returns null if unavailable. */
export async function resolveStorageUrl(ref: string | null | undefined): Promise<string | null> {
  if (!ref) return null;
  if (!isStoragePath(ref)) return ref;
  const cached = urlCache.get(ref);
  if (cached) return cached;
  if (!storage) return null;
  try {
    const url = await getDownloadURL(storageRef(storage, ref));
    urlCache.set(ref, url);
    return url;
  } catch {
    return null; // permission-denied / missing object — caller renders a dead state
  }
}

/** Hook form of resolveStorageUrl for use in components. */
export function useStorageUrl(ref: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!ref) return null;
    if (!isStoragePath(ref)) return ref;
    return urlCache.get(ref) ?? null;
  });
  useEffect(() => {
    let active = true;
    resolveStorageUrl(ref).then((u) => {
      if (active) setUrl(u);
    });
    return () => {
      active = false;
    };
  }, [ref]);
  return url;
}
